/**
 * Registry for per-entity-type standalone page renderers.
 *
 * Mirrors entityHoverCardRegistry but for full-page standalone rendering.
 * Each app that wants a custom standalone view registers here at startup.
 * Unknown types fall back to EntityViewApp (generic metadata + skeleton viewer).
 *
 * Usage (in your app's registerStandalone.ts, imported as a side effect):
 *
 *   entityStandaloneRegistry.register('Automation', {
 *     appId: 'automations',
 *     render: (ctx) => <AutomationsApp entity={ctx.entity} renderMode="standalone" standaloneMode="layout" viewerMode={ctx.role ?? 'viewer'} ... />,
 *   });
 *
 * Prefix matching is supported (dot-notation hierarchy):
 *   'Communication.Thread' → 'Communication' → generic fallback
 */

import React from 'react';
import { createFileLogger } from '../../utils/logger';
import { BaseEntityType } from '../../types/entities';
import type { CtaConfig } from '../PublicEntityFloatingPanel/PublicEntityFloatingPanel';

const log = createFileLogger('ENTITY_STANDALONE_REGISTRY', { enabled: false });

export interface StandaloneRendererContext {
  entity: BaseEntityType;
  /** Null when the visitor is not authenticated. */
  currentUser: { sub: string; name?: string } | null;
  /** True when the entity is publicly viewable (ACL subject:'public') and the visitor is not authenticated or not explicitly ACL'd. */
  isPublicViewer: boolean;
  /** True when currentUser is the entity owner. */
  isOwner: boolean;
  /** The ACL role for currentUser, or null when anonymous/not in ACL. */
  role: 'owner' | 'editor' | 'viewer' | 'commenter' | null;
}

export interface FloatingPanelOverrides {
  primaryCta?: CtaConfig;
  secondaryCta?: CtaConfig;
}

export interface StandaloneRenderer {
  /**
   * Render the entity detail view.
   * NOTE: Do NOT wrap in WindowWrapper — StandaloneEntityLayout provides the outer chrome.
   */
  render: (ctx: StandaloneRendererContext) => React.ReactNode;
  /** Optional custom loading state while the entity is being fetched. */
  renderLoading?: () => React.ReactNode;
  /**
   * The appId for this renderer (used by setPageContext so active-app logic works).
   * Matches the appType key in entityTypeMapping.ts (e.g. 'automations', 'docs').
   */
  appId?: string;
  /**
   * Optional per-entity-type overrides for the PublicEntityFloatingPanel shown to
   * anonymous visitors. Return undefined to use the default "Try River free" CTA.
   */
  floatingPanelProps?: (ctx: StandaloneRendererContext) => FloatingPanelOverrides | undefined;
}

class EntityStandaloneRegistry {
  private renderers = new Map<string, StandaloneRenderer>();

  register(entityType: string, renderer: StandaloneRenderer): void {
    log.info('[EntityStandaloneRegistry] register', { entityType, appId: renderer.appId });
    this.renderers.set(entityType, renderer);
  }

  /**
   * Look up a renderer by entity type with prefix fallback.
   * "Communication.Thread" → "Communication" → null (generic fallback)
   */
  getRenderer(entityType: string): StandaloneRenderer | null {
    if (!entityType) return null;

    // Exact match
    const exact = this.renderers.get(entityType);
    if (exact) {
      log.info('[EntityStandaloneRegistry] getRenderer hit (exact)', { entityType });
      return exact;
    }

    // Prefix match up the hierarchy
    const parts = entityType.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      const key = parts.slice(0, i).join('.');
      const renderer = this.renderers.get(key);
      if (renderer) {
        log.info('[EntityStandaloneRegistry] getRenderer hit (prefix)', { entityType, matchedKey: key });
        return renderer;
      }
    }

    log.info('[EntityStandaloneRegistry] getRenderer miss (using generic fallback)', { entityType });
    return null;
  }

  /** For debugging — list all registered entity types. */
  listRegistered(): string[] {
    return Array.from(this.renderers.keys());
  }
}

export const entityStandaloneRegistry = new EntityStandaloneRegistry();

if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__entityStandalone = {
    listRegistered: () => entityStandaloneRegistry.listRegistered(),
    getRenderer: (entityType: string) => entityStandaloneRegistry.getRenderer(entityType),
  };
}
